import { h, Fragment, cloneElement } from 'preact'
import { signal } from '@preact/signals'
import { useState, useEffect, useRef } from 'preact/hooks'
import { Slugify } from '../../js/modules/utilities.js'

import { TuileAccessoire } from '../tuile-accessoire/tuile-accessoire.jsx'
import { TuileArgument } from '../tuile-argument/tuile-argument.jsx'
import { TuileAiguillage } from '../tuile-aiguillage/tuile-aiguillage.jsx'
import { TuileAiguillage2 } from '../tuile-aiguillage-2/tuile-aiguillage-2.jsx'
import { TuileDevice } from '../tuile-device/tuile-device.jsx'
import { TuileEscda } from '../tuile-escda/tuile-escda.jsx'
import { TuileFallback } from '../tuile-fallback/tuile-fallback.jsx'
import { TuileImage } from '../tuile-image/tuile-image.jsx'
import { TuileImage2 } from '../tuile-image-2/tuile-image-2.jsx'
import { TuileMessage } from '../tuile-message/tuile-message.jsx'
import { TuileOffre } from '../tuile-offre/tuile-offre.jsx'
import { TuileOffre2 } from '../tuile-offre-2/tuile-offre-2.jsx'
import { TuileServicielle } from '../tuile-servicielle/tuile-servicielle.jsx'

// Swiper JS
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'
import { attributesAnalytics } from '../../js/modules/component'

export const vitrineState = signal('')

const getPriceSize = (items) => {
   for (const { items: tabItems } of items) {
      for (const { template, price } of tabItems) {
         if (template === 'device' && price && price.length > 6) {
            return true
         }
      }
   }
   return false
}

// Vérifie si une chaîne est une couleur hexadécimale valide
const isValidHexColor = (color) => {
   return /^#([0-9A-F]{3}){1,2}$/i.test(color)
}

// Remplace toutes les couleurs dans un SVG
const replaceColorInSvg = (element, newColor, imutableColors = ['white']) => {
   if (!element || !isValidHexColor(newColor)) {
      return element
   }

   // Fonction récursive pour cloner et modifier les éléments
   const cloneWithNewColor = (el) => {
      if (!el || typeof el !== 'object') {
         return el
      }

      // Si l'élément a une prop fill, la remplacer par newColor (sauf si c'est "none" ou dans imutableColors)
      const newProps = {}
      if (el.props) {
         Object.keys(el.props).forEach((key) => {
            if (key === 'fill' && el.props[key] && el.props[key] !== 'none') {
               const currentColor = el.props[key].toLowerCase()
               const shouldReplace = !imutableColors.some((immutableColor) => currentColor === immutableColor.toLowerCase())
               newProps[key] = shouldReplace ? newColor : el.props[key]
            } else if (key === 'children') {
               // Traiter récursivement les enfants
               const children = el.props.children
               if (Array.isArray(children)) {
                  newProps[key] = children.map((child) => cloneWithNewColor(child))
               } else if (children && typeof children === 'object') {
                  newProps[key] = cloneWithNewColor(children)
               } else {
                  newProps[key] = children
               }
            } else {
               newProps[key] = el.props[key]
            }
         })
      }

      return cloneElement(el, newProps)
   }

   return cloneWithNewColor(element)
}

/**
 * Filtre et trie les items selon le paramètre tabDisplay
 * @param {Array} items - Les items à filtrer et trier
 * @param {Array} tabDisplay - Les IDs des items à afficher, dans l'ordre désiré
 * @returns {Array} - Les items filtrés et triés
 */
const processTabDisplay = (items, tabDisplay) => {
   if (!tabDisplay || tabDisplay.length === 0) {
      return items.filter((item) => item.display === true)
   }

   // Marquer les items à afficher selon tabDisplay
   const processedItems = items.map((item) => ({
      ...item,
      display: tabDisplay.includes(item.id),
   }))

   // Trier les items selon l'ordre défini dans tabDisplay
   const sortedItems = processedItems.sort((a, b) => {
      const indexA = tabDisplay.indexOf(a.id)
      const indexB = tabDisplay.indexOf(b.id)
      return indexA - indexB
   })

   // Filtrer pour ne garder que les items avec display à true
   return sortedItems.filter((item) => item.display === true)
}

export const Vitrine = ({
   cacheKey,
   grid,
   pictoActiveColor = '',
   pictoColor = '',
   tabColor = '',
   noBackground = false,
   hideTitle = false,
   items,
   liensVoirTout = true,
   tabDisplay = [],
   title = '',
}) => {
   //       // Traitement du tabDisplay : filtrer et trier les items
   items = processTabDisplay(items, tabDisplay)

   // Pictos onglets
   const pictoTabOriginal = {
      'onglet-internet': (
         <svg width="26" height="50" viewBox="0 0 26 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1713_9191)">
               <path d="M24.1747 44.434V5.53728C24.1747 4.09555 23.1874 2.89411 21.8311 2.59375V47.3675C23.1874 47.0671 24.1747 45.8557 24.1747 44.4239V44.434Z" fill="white" />
               <path
                  d="M21.791 0.736575C21.811 0.826683 21.8309 0.916791 21.8309 1.0069V2.59881C23.1972 2.89917 24.1746 4.10061 24.1746 5.54234V44.439C24.1746 45.8707 23.1873 47.0822 21.8309 47.3825V49.0045C21.8309 49.0846 21.8209 49.1647 21.801 49.2448C24.2045 48.9344 25.9997 46.8819 25.9997 44.449V5.53233C25.9997 3.06937 24.2045 1.03693 21.791 0.726562V0.736575Z"
                  fill="black"
               />
               <path d="M1.8252 5.52189V44.4186C1.8252 45.7702 2.69286 46.9116 3.92953 47.292V2.64844C2.69286 3.02889 1.8252 4.18028 1.8252 5.52189Z" fill="white" />
               <path
                  d="M3.92942 47.3093C2.69275 46.9288 1.82509 45.7875 1.82509 44.4358V5.52914C1.82509 4.18753 2.70272 3.03615 3.92942 2.6557V1.01373C3.92942 0.93363 3.93939 0.843521 3.95934 0.773438C1.67549 1.18393 0 3.17632 0 5.53916V44.4358C0 46.8087 1.66552 48.781 3.94937 49.2015C3.93939 49.1415 3.92942 49.0814 3.92942 49.0113V47.3093Z"
                  fill="black"
               />
               <path d="M20.0055 1.82812H5.75391V48.1838H20.0055V1.82812Z" fill="white" />
               <path
                  d="M21.8315 47.3768V1.01121C21.8315 0.921105 21.8215 0.820985 21.7916 0.740889C21.6719 0.320384 21.283 0 20.8242 0H4.93698C4.46824 0 4.06931 0.330397 3.95961 0.760913C3.93966 0.841009 3.92969 0.921105 3.92969 1.0012V48.9888C3.92969 49.0489 3.92969 49.1189 3.94963 49.179C4.03939 49.6496 4.44829 50 4.93698 50H20.8242C21.2929 50 21.6919 49.6696 21.8016 49.2291C21.8215 49.149 21.8315 49.0789 21.8315 48.9888V47.3668V47.3768ZM20.0064 48.1778H5.75477V1.8322H20.0064V48.1878V48.1778Z"
                  fill="black"
               />
               <path d="M14.5509 0H11.4492V8.09972H14.5509V0Z" fill="#F15E00" />
               <path
                  d="M12.9953 32.6563C14.2181 32.6563 15.2093 31.6612 15.2093 30.4336C15.2093 29.2061 14.2181 28.2109 12.9953 28.2109C11.7725 28.2109 10.7812 29.2061 10.7812 30.4336C10.7812 31.6612 11.7725 32.6563 12.9953 32.6563Z"
                  fill="#F15E00"
               />
               <path
                  d="M12.9953 26.2656C14.2181 26.2656 15.2093 25.2705 15.2093 24.043C15.2093 22.8154 14.2181 21.8203 12.9953 21.8203C11.7725 21.8203 10.7812 22.8154 10.7812 24.043C10.7812 25.2705 11.7725 26.2656 12.9953 26.2656Z"
                  fill="#F15E00"
               />
               <path
                  d="M12.9953 19.8906C14.2181 19.8906 15.2093 18.8955 15.2093 17.668C15.2093 16.4404 14.2181 15.4453 12.9953 15.4453C11.7725 15.4453 10.7812 16.4404 10.7812 17.668C10.7812 18.8955 11.7725 19.8906 12.9953 19.8906Z"
                  fill="#F15E00"
               />
            </g>
            <defs>
               <clipPath id="clip0_1713_9191">
                  <rect width="26" height="50" fill="white" />
               </clipPath>
            </defs>
         </svg>
      ),
      'onglet-mobile': (
         <svg width="29" height="50" viewBox="0 0 29 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1713_9202)">
               <path
                  d="M5.8516 2C3.18381 2 1 4.47138 1 7.50162V42.4984C1 45.5286 3.17201 48 5.8516 48H24.1484C26.8162 48 29 45.5286 29 42.4984V12.2208L20.9376 2H5.8516Z"
                  fill="white"
               />
               <path
                  d="M20.7703 0H6.06344C2.72147 0 0 3.31582 0 7.36987V42.6301C0 46.6967 2.72147 50 6.06344 50H22.9366C26.2785 50 29 46.6967 29 42.6301V9.52502L20.7703 0ZM27.4107 42.6426C27.4107 45.6957 25.4077 48.1857 22.9366 48.1857H6.06344C3.60323 48.1857 1.58934 45.6957 1.58934 42.6426V7.36987C1.58934 4.31682 3.59234 1.82683 6.06344 1.82683H19.9756L27.4107 10.1256V42.6301V42.6426Z"
                  fill="black"
               />
               <path
                  d="M12.9877 29.3594H7.7377C6.785 29.3594 6.0127 30.1317 6.0127 31.0844V41.5844C6.0127 42.5371 6.785 43.3094 7.7377 43.3094H12.9877C13.9404 43.3094 14.7127 42.5371 14.7127 41.5844V31.0844C14.7127 30.1317 13.9404 29.3594 12.9877 29.3594Z"
                  fill="#F15E00"
               />
            </g>
            <defs>
               <clipPath id="clip0_1713_9202">
                  <rect width="29" height="50" fill="white" />
               </clipPath>
            </defs>
         </svg>
      ),
      'onglet-terminaux': (
         <svg width="26" height="50" viewBox="0 0 26 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_1713_9187)">
               <path
                  d="M23.0079 1.82812H2.99347C2.34609 1.82812 1.82129 2.35433 1.82129 3.00343V46.9894C1.82129 47.6385 2.34609 48.1647 2.99347 48.1647H23.0079C23.6552 48.1647 24.18 47.6385 24.18 46.9894V3.00343C24.18 2.35433 23.6552 1.82812 23.0079 1.82812Z"
                  fill="white"
               />
               <path
                  d="M22.9947 0H2.99281C1.34676 0 0 1.35034 0 3.00075V46.9992C0 48.6497 1.34676 50 2.99281 50H23.0072C24.6532 50 26 48.6497 26 46.9992V3.00075C26 1.35034 24.6532 0 23.0072 0H22.9947ZM24.1669 46.9992C24.1669 47.6494 23.6432 48.1745 22.9947 48.1745H2.99281C2.34437 48.1745 1.82062 47.6494 1.82062 46.9992V3.00075C1.82062 2.35059 2.34437 1.82546 2.99281 1.82546H23.0072C23.6556 1.82546 24.1794 2.35059 24.1794 3.00075V46.9992H24.1669Z"
                  fill="black"
               />
               <path
                  d="M12.9933 45.887C14.2123 45.887 15.2005 44.8962 15.2005 43.674C15.2005 42.4518 14.2123 41.4609 12.9933 41.4609C11.7743 41.4609 10.7861 42.4518 10.7861 43.674C10.7861 44.8962 11.7743 45.887 12.9933 45.887Z"
                  fill="#F15E00"
               />
            </g>
            <defs>
               <clipPath id="clip0_1713_9187">
                  <rect width="26" height="50" fill="white" />
               </clipPath>
            </defs>
         </svg>
      ),
   }

   // Appliquer la couleur personnalisée si elle est valide
   const pictoTab = isValidHexColor(pictoColor)
      ? Object.fromEntries(Object.entries(pictoTabOriginal).map(([key, svg]) => [key, replaceColorInSvg(svg, pictoColor)]))
      : pictoTabOriginal

   // Appliquer la couleur active si elle est valide
   const pictoTabActive = isValidHexColor(pictoActiveColor)
      ? Object.fromEntries(Object.entries(pictoTabOriginal).map(([key, svg]) => [key, replaceColorInSvg(replaceColorInSvg(svg, pictoColor, ['white']), pictoActiveColor, ['#000000'])]))
      : pictoTab

   // Fonction pour obtenir le picto approprié selon l'état actif
   const getPictoForTab = (tab) => {
      const pictoKey = tab.picto || tab.id
      return tab.id === selectedTab ? pictoTabActive[pictoKey] : pictoTab[pictoKey]
   }

   // SIGNALS
   // Récupérer les data et les injecter au bon endroit dans la tuile dont l'id correspond du nom du signal
   const positionId = Object.keys(vitrineState.value)[0]
   if (vitrineState.value !== '' && vitrineState.value[positionId] !== undefined) {
      const selectedTile = items[0].items.find((item) => item.id === positionId)
      if (selectedTile) {
         Object.assign(selectedTile, vitrineState.value[positionId])
      }
   }

   let swiper
   const priceSmall = getPriceSize(items)
   const [isNavigating, setIsNavigating] = useState(false)
   const [selectedTab, setSelectedTab] = useState(() => {
      const hash = window.location.hash.substring(1)
      const index = items.findIndex((item) => Slugify(item.title) === hash)
      if (index === -1) return items[0].id
      return items[index].id
   })

   // Gestion de l'accessibilité au tab et aux touches flèches
   const listRef = useRef(null)

   useEffect(() => {
      const listElement = listRef.current
      if (listElement !== null) {
         listElement.querySelectorAll("[role='tab']").forEach((tab, index, tabs) => {
            tab.addEventListener('keydown', (e) => {
               let newIndex
               if (e.key === 'ArrowRight') {
                  newIndex = (index + 1) % tabs.length
               } else if (e.key === 'ArrowLeft') {
                  newIndex = (index - 1 + tabs.length) % tabs.length
               }
               if (newIndex !== undefined) {
                  tabs[newIndex].focus()
                  tabs[newIndex].click()
               }
            })
         })
      }

      swiper = new Swiper('.swiper', {
         modules: [Navigation, Pagination],
         navigation: {
            enabled: false,
            nextEl: '.swiper-button-aloha-next',
            prevEl: '.swiper-button-aloha-prev',
         },
         pagination: {
            enabled: true,
            el: '.swiper-pagination',
            clickable: true,
            bulletClass: 'swiper-pagination-aloha',
            bulletActiveClass: 'swiper-pagination-aloha-active',
            horizontalClass: 'swiper-pagination-aloha-horizontal',
            renderBullet: function (index, className) {
               return '<span class="' + className + '"><span></span></span>'
            },
         },
         direction: 'horizontal',
         preventClicks: false,
         loop: false,
         slidesOffsetBefore: 15,
         slidesOffsetAfter: 15,
         spaceBetween: 30,
         slidesPerView: 'auto',
         breakpoints: {
            // when window width is >= 736px
            736: {
               pagination: {
                  enabled: true,
               },
               navigation: {
                  enabled: true,
               },
               slidesOffsetBefore: 30,
            },
            // when window width is >= 960px
            960: {
               pagination: {
                  enabled: true,
               },
               navigation: {
                  enabled: true,
               },
               slidesOffsetBefore: 45,
            },
            // when window width is >= 1200px
            1200: {
               navigation: {
                  enabled: false,
               },
               pagination: {
                  enabled: false,
               },
               slidesOffsetBefore: 0,
               allowTouchMove: false,
            },
         },
      })

      const handleResize = () => {
         swiper.forEach((s) => {
            s?.slideTo(0)
         })
      }

      // Gestion de la navigation avec les flèches du navigateur
      const handleHistoryChange = () => {
         const hash = window.location.hash.substring(1)
         const index = items.findIndex((item) => Slugify(item.title) === hash)
         setIsNavigating(true)
         if (index === -1) {
            setSelectedTab(items[0].id)
         } else {
            setSelectedTab(items[index].id)
         }
      }

      window.addEventListener('popstate', handleHistoryChange)
      window.addEventListener('resize', handleResize)

      return () => {
         window.removeEventListener('popstate', handleHistoryChange)
         window.removeEventListener('resize', handleResize)
      }
   }, [])

   useEffect(() => {
      // Update le hash de l'URL avec l'onglet sélectionné (ne pas exécuter si on est en train de naviguer avec les flèches du navigateur)
      if (!isNavigating) {
         if (selectedTab && items.findIndex((item) => item.id === selectedTab) !== 0) {
            const selectedTabItem = items.find((item) => item.id === selectedTab)
            window.history.pushState(null, null, `#${Slugify(selectedTabItem.title)}`)
         } else {
            window.history.pushState(null, null, ' ')
         }
      }
      setIsNavigating(false)
   }, [selectedTab])

   return (
      <div
         class={`component-vitrine pb-0 pb-lg-2 ${noBackground ? '' : 'bg-gray2'}`}
         data-cache-key={cacheKey}
         style={isValidHexColor(tabColor) ? { '--tab-color': tabColor } : {}}
      >
         {title && !hideTitle ? (
            <div class="container-fluid pt-2 pt-md-3">
               <h2 class="no-signature text-center mw-100 mb-0">{title}</h2>
            </div>
         ) : (
            <h2 class="sr-only">{title}</h2>
         )}
         {items.length > 1 && (
            <div class="container-fluid pt-2 pt-md-3">
               <nav class="tabs-menu ob1-menu-page ob1-menu-page-center mb-1">
                  <ul class="nav o-nav-light" ref={listRef} role="tablist">
                     {items.map((tab, index) => (
                        <li key={index} role="presentation" class={`mb-1 nav-item ${tab.id === selectedTab ? 'active' : ''}`}>
                           <button
                              role="tab"
                              aria-controls={`vitrine-tab${index}-content`}
                              aria-selected={tab.id === selectedTab ? 'true' : 'false'}
                              onClick={() => setSelectedTab(tab.id)}
                              class="nav-link btn"
                              id={`vitrine-tab${index}`}
                              tabIndex={tab.id === selectedTab ? '0' : '-1'}
                              {...attributesAnalytics(tab.analytics)}
                           >
                              {tab.id && getPictoForTab(tab)}
                              {tab.title}
                           </button>
                        </li>
                     ))}
                  </ul>
               </nav>
            </div>
         )}
         <div class={`tabs-container ${items.length > 1 ? '' : 'pt-1 pt-md-2'}`}>
            {items.map((tab, index) => (
               <Fragment key={index}>
                  {!hideTitle && (
                     <div class="container-fluid d-flex justify-content-between mt-1 mt-lg-0">
                        {tab.title && <h2 class="h3 tab-title mb-0 mt-0">{tab.title}</h2>}
                        {liensVoirTout && tab.lien_aiguillage && (
                           <a href={tab.lien_aiguillage.URL} class="lien-voir-tout o-link-arrow d-xl-none" {...attributesAnalytics(tab.lien_aiguillage.analytics)}>
                              {tab.lien_aiguillage.label}
                           </a>
                        )}
                     </div>
                  )}
                  <div class="container-fluid only-after-1200">
                     <div
                        class={`tab-content grid-${grid.name} ${tab.id === selectedTab ? 'active' : ''}`}
                        role="tabpanel"
                        id={`vitrine-tab${index}-content`}
                        aria-labelledby={`vitrine-tab${index}`}
                     >
                        <div class="swiper">
                           <div class="swiper-wrapper">
                              {tab.items.map((tile, index) => (
                                 <div key={index} class={`swiper-slide tile tile${index + 1} ${grid.tilesSize[index]}`}>
                                    {renderTile(tile, priceSmall)}
                                 </div>
                              ))}
                           </div>

                           <div class="swiper-pagination" aria-hidden="true"></div>

                           <div class="swiper-button-aloha swiper-button-aloha-prev" aria-hidden="true">
                              <span class="icon-arrow-previous"></span>
                           </div>
                           <div class="swiper-button-aloha swiper-button-aloha-next" aria-hidden="true">
                              <span class="icon-arrow-next"></span>
                           </div>
                        </div>
                     </div>
                  </div>
               </Fragment>
            ))}
         </div>
      </div>
   )
}

const renderTile = (props, priceSmall) => {
   const { template, ...restOfProps } = props

   if (template === 'device') {
      restOfProps.price_small = priceSmall
   }
   const components = {
      advise: Placeholder,
      argument: TuileArgument,
      aiguillage: TuileAiguillage,
      aiguillage_2: TuileAiguillage2,
      device: TuileDevice,
      accessoire: TuileAccessoire,
      escda: TuileEscda,
      fallback: TuileFallback,
      image: TuileImage,
      image_2: TuileImage2,
      message: TuileMessage,
      multiservice: 'harmony-multiservice-tile',
      offre: TuileOffre,
      offre_2: TuileOffre2,
      servicielle: TuileServicielle,
   }

   const Component = components[template]
   return Component ? <Component {...restOfProps} /> : null
}

const Placeholder = ({ id }) => {
   return <div class="tile-placeholder" id={id}></div>
}
